import React, { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../store';
import {
  closeOnboardingChecklist,
  completeOnboardingStep,
  markFirstRunCompleted,
} from '../../store/slices/helpSlice';
import { ONBOARDING_STEPS } from '../../lib/help/onboardingSteps';
import { trackHelpEvent } from '../../lib/help/helpAnalytics';
import { CheckCircle2, Circle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const OnboardingChecklistCard: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const open = useAppSelector((state) => state.help.onboardingChecklistOpen);
  const progress = useAppSelector((state) => state.help.onboardingProgress);

  const completedCount = useMemo(
    () => ONBOARDING_STEPS.filter((step) => progress[step.id] === 'completed').length,
    [progress]
  );

  if (!open) return null;

  const allCompleted = completedCount === ONBOARDING_STEPS.length;

  return (
    <aside
      className="fixed bottom-24 right-4 z-[75] w-[min(380px,calc(100vw-1.5rem))] rounded-xl border border-[rgba(176,210,255,0.24)] bg-[rgba(34,76,138,0.95)] p-3 shadow-[0_24px_52px_rgba(7,16,34,0.62)]"
      aria-label="First-time onboarding checklist"
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <div>
          <h3 className="m-0 text-sm font-semibold text-text-primary">
            {t('help.firstTimeChecklist', { defaultValue: 'First-time checklist' })}
          </h3>
          <p className="m-0 text-xs text-text-primary/80">
            {t('help.completedCount', {
              completed: completedCount,
              total: ONBOARDING_STEPS.length,
              defaultValue: `${completedCount}/${ONBOARDING_STEPS.length} completed`,
            })}
          </p>
        </div>
        <button
          type="button"
          onClick={() => dispatch(closeOnboardingChecklist())}
          className="rounded p-1 text-text-muted hover:text-text-primary"
          aria-label={t('help.closeChecklist', { defaultValue: 'Close checklist' })}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <ul className="space-y-2">
        {ONBOARDING_STEPS.map((step) => {
          const done = progress[step.id] === 'completed';
          return (
            <li
              key={step.id}
              className="flex items-start gap-2 rounded-md border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.03)] px-2.5 py-2"
            >
              {done ? (
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-accent-bullish" />
              ) : (
                <Circle className="mt-0.5 h-4 w-4 shrink-0 text-text-muted" />
              )}
              <div className="min-w-0 flex-1">
                <p className="m-0 text-xs font-semibold text-text-primary">
                  {t(`help.steps.${step.id}.title`)}
                </p>
                <p className="m-0 text-xs text-text-primary/80">
                  {t(`help.steps.${step.id}.description`)}
                </p>
              </div>
              {!done && (
                <button
                  type="button"
                  className="rounded border border-[rgba(255,255,255,0.18)] bg-[rgba(255,255,255,0.06)] px-2 py-1 text-xs font-medium text-text-primary hover:bg-[rgba(255,255,255,0.12)]"
                  onClick={() => {
                    dispatch(completeOnboardingStep(step.id));
                    trackHelpEvent('onboarding_step_completed', { stepId: step.id });
                  }}
                >
                  {t('help.markDone', { defaultValue: 'Mark done' })}
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {allCompleted && (
        <button
          type="button"
          className="mt-3 w-full rounded-md bg-accent-primary/20 px-3 py-2 text-xs font-semibold text-accent-primary hover:bg-accent-primary/30"
          onClick={() => {
            dispatch(markFirstRunCompleted());
            dispatch(closeOnboardingChecklist());
            trackHelpEvent('onboarding_completed', { source: 'checklist' });
          }}
        >
          {t('help.finishOnboarding', { defaultValue: 'Finish onboarding' })}
        </button>
      )}
    </aside>
  );
};

export default OnboardingChecklistCard;
